import { Component, OnDestroy, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { UsersService } from 'src/app/parameters/users/users.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { FeaturePageLocalStorageKeyEnum } from 'src/app/shared/components/search-bar/feature-page-local-storage-key.enum';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { FeatureTourStepEnum } from './shared/feature-tour-step.enum';
import { FeatureTourService } from './shared/feature-tour.service';
import { MessageTypeEnum } from './shared/message-type.enum';
import { SearchService } from './shared/search.service';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SearchComponent implements OnDestroy {
  searchBarPopoverDisplayed = false;
  advancedSearchPopoverDisplayed = false;
  massActionsPopoverDisplayed = false;
  FeatureTourStepEnum = FeatureTourStepEnum;
  MessageTypeEnum = MessageTypeEnum;
  FeaturePageLocalStorageKeyEnum = FeaturePageLocalStorageKeyEnum;

  dateRangeString: string;
  teams: Choice[] = [];
  allAccountsIds = [];
  languages: Choice[] = [];
  searching: boolean;

  private subscriptions: Subscription = new Subscription();
  private previousChannelsSelected = [];

  @ViewChild('filtersFormTemplateRef', { static: true }) filtersFormTemplateRef: TemplateRef<any>;

  constructor(
    public searchService: SearchService,
    public customRouter: CustomRouter,
    public alcmeonContextService: AlcmeonContextService,
    public featureTourService: FeatureTourService,
    public searchBarService: SearchBarService,
    private usersService: UsersService,
    private basicService: BasicService,
    private dialog: MatDialog,
    private sitesService: SitesService
  ) {
    this.subscriptions.add(
      this.searchService.form
        .get('messageContent.containingText')
        .valueChanges.pipe(debounceTime(300))
        .subscribe(value => {
          if (value !== null && (!value || value.length >= 3)) {
            this.searchService.retrieveTasksFromForm();
          }
        })
    );

    this.subscriptions.add(
      this.searchBarService.searchAndSortBS.pipe(debounceTime(300)).subscribe(value => {
        if (value) {
          this.extractChannelsFromSideBarFilter();
          this.searchService.retrieveTasksFromForm();
        }
      })
    );

    this.getReferences();
    this.extractChannelsFromSideBarFilter();
    this.listenFormAndEncode();
    this.listenFeatureTour();
    this.instantiateFromLocalStorage();
  }

  clearSearch() {
    this.searchService.form.get('messageContent.containingText').reset();
    this.searchService.retrieveTasksFromForm();
  }

  modifyFilters() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: ['small-size', 'app-search'],
      disableClose: true,
      data: {
        title: this.basicService.translate('search/advanced-search/title'),
        templateRef: this.filtersFormTemplateRef,
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('ok'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.searchService.retrieveTasksFromForm();
      }
    });
  }

  private extractChannelsFromSideBarFilter() {
    let filtersChanged = false;
    try {
      filtersChanged =
        JSON.stringify(this.previousChannelsSelected) !==
        JSON.stringify(this.searchBarService.filtersMap.get('siteTypes'));
    } catch {
      filtersChanged = false;
    }

    if (filtersChanged) {
      this.previousChannelsSelected = [].concat(...(this.searchBarService.filtersMap.get('siteTypes') || []));
      if (!this.searchBarService.filtersMap.has('siteTypes')) {
        this.searchService.parameters.sites_ids_queues_types = undefined;
      } else {
        const keys: string[] = Array.from(this.searchBarService.filtersMap.get('siteTypes'));
        this.searchService.parameters.sites_ids_queues_types = (keys || []).reduce(
          (sites_ids_queues_types: [number, number][], key: string) => {
            const [strategy, taskType] = (key || '').split('|');
            const sites = this.sitesService.sitesV2.filter(({ type }) => +strategy === type);
            (sites || []).forEach(({ id }) => sites_ids_queues_types.push([id, +taskType]));
            return sites_ids_queues_types;
          },
          []
        );
        if (
          !this.searchService.parameters.sites_ids_queues_types ||
          !this.searchService.parameters.sites_ids_queues_types?.length
        ) {
          this.searchService.parameters.sites_ids_queues_types = undefined;
        }
      }
    }
  }

  private getReferences() {
    const routingTagExists = this.alcmeonContextService.getCompany().tags_groups.some(obj => obj.is_routing === 1);
    this.usersService
      .getTeams(routingTagExists)
      .subscribe(
        teams =>
          (this.teams = teams
            .map(({ tag_id, name }) => ({ id: tag_id, label: name }))
            .sort((a, b) => (a.label || '').localeCompare(b.label || '')))
      );

    this.allAccountsIds = this.searchService.sites.map(({ id }) => id);
    this.previousChannelsSelected = [].concat(this.searchBarService.filtersMap.get('siteTypes') || []);
  }

  private listenFeatureTour() {
    this.subscriptions.add(
      this.featureTourService.featureTourStepBS.subscribe(value => {
        if (value) {
          this.searchBarPopoverDisplayed = value === FeatureTourStepEnum.SEARCH_BAR;
          this.advancedSearchPopoverDisplayed = value === FeatureTourStepEnum.ADVANCED_SEARCH;
          this.massActionsPopoverDisplayed = value === FeatureTourStepEnum.MASS_ACTIONS;
        } else {
          this.searchBarPopoverDisplayed = false;
          this.advancedSearchPopoverDisplayed = false;
          this.massActionsPopoverDisplayed = false;
        }
      })
    );
  }

  private listenFormAndEncode() {
    this.subscriptions.add(
      this.searchService.form.get('messageAttributes.receiptDate').valueChanges.subscribe(value => {
        const result = this.searchService.getDateRangeFromChoice(value);
        this.searchService.parameters.receiptDate = value;
        if (result) {
          const { start, end } = result;
          this.searchService.parameters.since = start.toISOString();
          this.searchService.parameters.until = end.toISOString();
        } else {
          this.searchService.parameters.since = undefined;
          this.searchService.parameters.until = undefined;
        }
      })
    );

    this.subscriptions.add(
      this.searchService.form.get('messageAttributes.accounts').valueChanges.subscribe(value => {
        this.searchService.parameters.sites_ids = value?.length ? value : undefined;
      })
    );

    this.subscriptions.add(
      this.searchService.form.get('messageAttributes.statuses').valueChanges.subscribe(value => {
        this.searchService.parameters.states = value?.length ? value : undefined;
      })
    );

    this.subscriptions.add(
      this.searchService.form.get('messageContent.containingText').valueChanges.subscribe(value => {
        this.searchService.parameters.message_includes = value || undefined;
      })
    );

    this.subscriptions.add(
      this.searchService.form.get('messageContent.notContainingText').valueChanges.subscribe(value => {
        this.searchService.parameters.message_excludes = value || undefined;
      })
    );

    this.subscriptions.add(
      this.searchService.form.get('actions.date').valueChanges.subscribe(value => {
        this.searchService.parameters.actionDate = value;
        const result = this.searchService.getDateRangeFromChoice(value);
        if (result) {
          const { start, end } = result;
          this.searchService.parameters.action_date_since = start.toISOString();
          this.searchService.parameters.action_date_until = end.toISOString();
        } else {
          this.searchService.parameters.action_date_since = undefined;
          this.searchService.parameters.action_date_until = undefined;
        }
      })
    );

    this.subscriptions.add(
      this.searchService.form.get('actions.types').valueChanges.subscribe(value => {
        this.searchService.parameters.action_types = value || undefined;
      })
    );

    this.subscriptions.add(
      this.searchService.form.get('actions.processedBy').valueChanges.subscribe(value => {
        this.searchService.parameters.action_author_ids = value || undefined;
      })
    );

    this.subscriptions.add(
      this.searchService.form.get('actions.hideMessagesProcessedOnlyByBots').valueChanges.subscribe(value => {
        if (value) {
          this.searchService.parameters.action_author_ids = (this.searchService.advisors || []).map(
            ({ id }) => id as number
          );
        } else {
          this.searchService.parameters.action_author_ids =
            this.searchService.form.value.actions.processedBy || undefined;
        }
      })
    );
  }

  private instantiateFromLocalStorage() {
    try {
      const parameters = JSON.parse(
        localStorage.getItem(`${this.alcmeonContextService.companyId}-search-page-filters`)
      );
      const {
        sites_ids,
        receiptDate,
        sites_ids_queues_types,
        states,
        message_includes,
        message_excludes,
        actionDate,
        action_types,
        action_author_ids,
      } = parameters;
      const siteTypesSet = new Set();
      (sites_ids_queues_types || []).forEach(type => siteTypesSet.add(type));
      this.searchBarService.filtersMap.set('siteTypes', siteTypesSet);
      this.searchService.form.patchValue({
        messageAttributes: {
          receiptDate,
          accounts: sites_ids || [],
          statuses: states || [],
        },
        messageContent: {
          containingText: message_includes,
          notContainingText: message_excludes,
        },
        actions: {
          date: actionDate,
          types: action_types || [],
          processedBy: action_author_ids || [],
          hideMessagesProcessedOnlyByBots: null,
        },
      });
    } catch (error) {
      // Do nothing
    }
    this.searchService.searchFiltersBS.next(this.searchService.computeSearchFiltersMap());
    this.searchService.retrieveTasksFromForm();
  }

  ngOnDestroy(): void {
    this.searchService.searchFiltersBS.next(new Map());
    this.subscriptions?.unsubscribe();
    this.searchBarService.onDidLeave();
  }
}
